/**
 * 上映スケジュールAI ステップ3：タイムチャート作成の共通ヘルパー（DEV-14983）
 * 時刻は営業日基準の「分」で扱う（25:30 = 1530 分。最大 30:00）
 */

// 対象期間の最大日数（開始日・終了日を含む）。バックエンドも同じ上限でチェックする
export const MAX_PERIOD_DAYS = 14;
// 期間を指定せずに開いたときの日数（直近の金曜日から 1 週間）
export const DEFAULT_PERIOD_DAYS = 7;
// 終了時刻の上限（30:00 = 1800 分）
export const TIME_MAX_MINUTES = 30 * 60;
// ドラッグ・時刻候補の刻み（分）
export const SNAP_MINUTES = 5;
// 曜日表示
const WEEKDAY_LABELS = ["日", "月", "火", "水", "木", "金", "土"];

// 違反種別の表示名
export const VIOLATION_TYPE_LABELS = {
  OVERLAP: "時間重複",
  CLEANING: "清掃間隔不足",
  OUT_OF_HOURS: "営業時間外",
  FORMAT: "非対応バージョン",
  SCREEN_DISABLED: "停止中スクリーン",
  MOVIE_REMOVED: "作品・バージョン削除済",
  OUT_OF_PERIOD: "上映期間外",
};

// ルール判定結果の表示名と色
export const RULE_CHECK_STATUS = {
  SATISFIED: { label: "満たす", type: "success" },
  UNSATISFIED: { label: "未達", type: "danger" },
  UNDETERMINED: { label: "判定不可", type: "info" },
};

// ルールの由来
export const RULE_SOURCE_OPTIONS = [
  { value: "DISTRIBUTOR", label: "配給要望" },
  { value: "CINEMA", label: "劇場要望" },
];

// ルールの種類
export const RULE_KIND_OPTIONS = [
  { value: "MIN_SHOWS", label: "最低回数" },
  { value: "MAX_SHOWS", label: "最大回数" },
  { value: "TIME_PREF", label: "時間帯指定" },
  { value: "SCREEN_PREF", label: "スクリーン指定" },
  // 「多めに」「中心に」などを割合（多め 50%・中心 70%）に換算したもの。照合できるが必須ではない
  { value: "SHARE_PREF", label: "配分指定" },
  { value: "OTHER", label: "その他" },
];

// 回数の単位
export const COUNT_UNIT_OPTIONS = [
  { value: "TOTAL", label: "期間合計" },
  { value: "PER_DAY", label: "1日あたり" },
];

/**
 * 配給会社の要望なのに必須条件（hard）として扱われていないルールか（DEV-14984）
 * AI が判定不可（AMBIGUOUS）・その他（OTHER）と解析した場合や、サーバー側の検証（期間外・バージョン不一致など）で
 * 必須から外れた場合は、AI が原文を参考に判断するだけで回数が満たされる保証はない
 */
export const isDistributorSoftRule = (r) => r?.source === "DISTRIBUTOR" && !r.hard
  && (r.resolve === "AMBIGUOUS" || r.kind === "OTHER");

/**
 * 照合結果のうち、配給会社の必須でない要望で判定できなかったもの（実行可能でも要確認として出す）
 * hard は照合結果になければルール側の値を渡す
 */
export const isDistributorUndeterminedCheck = (c, hard = c?.hard) => c?.source === "DISTRIBUTOR" && !hard
  && c.status === "UNDETERMINED";

/**
 * value → label の取得（未定義は値そのまま）
 */
export const optionLabel = (options, value) => options.find((o) => o.value === value)?.label ?? value ?? "";

/**
 * 前提ステップ（①劇場・スクリーン設定 / ②上映作品・バージョン登録）が未完了のエラーか判定する。
 * バックエンドの専用コード（4601＝ステップ1、4602＝ステップ2）を優先し、
 * コードがない場合（旧バックエンド・生成ジョブの失敗メッセージ）は文言で判定する
 * 戻り値："setting" | "movie" | null（前提ステップと無関係なエラー）
 */
const PREREQ_ERROR_CODES = { 4601: "setting", 4602: "movie" };
const PREREQ_ERROR_PATTERNS = [
  { step: "setting", re: /劇場設定が未登録|スクリーンが登録されていません|上映可能バージョンが登録されていません/ },
  {
    step: "movie",
    re: /上映作品が登録されていません|上映時間が登録されていません|上映バージョンが登録されていません|(?:今週|この期間に?|対象期間に?)上映する作品がありません/,
  },
];
export const prereqStepOf = (msg, code = null) => PREREQ_ERROR_CODES[code]
  || PREREQ_ERROR_PATTERNS.find((p) => p.re.test(msg || ""))?.step
  || null;

/**
 * "HH:mm"（24 以降も可）→ 分。不正値は null
 */
export const toMinutes = (time) => {
  if (typeof time !== "string") return null;
  const m = /^(\d{1,2}):(\d{2})$/.exec(time.trim());
  if (!m) return null;
  const minute = Number(m[2]);
  if (minute > 59) return null;
  return Number(m[1]) * 60 + minute;
};

/**
 * 分 → "HH:mm"（24:00 以降もそのまま 25:30 のように表記）
 */
export const toTime = (minutes) => {
  if (minutes === null || minutes === undefined || Number.isNaN(minutes)) return "";
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
};

/**
 * 5 分刻みに丸める
 */
export const snapMinutes = (minutes) => Math.round(minutes / SNAP_MINUTES) * SNAP_MINUTES;

// ==================== 日付 ====================

/**
 * yyyy-MM-dd → ローカル日付（タイムゾーンずれを避けるため Date.parse は使わない）
 */
export const parseDate = (str) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(str || "");
  if (!m) return null;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
};

/**
 * Date → yyyy-MM-dd
 */
export const formatDate = (date) => {
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

/**
 * yyyy-MM-dd に日数を足す
 */
export const addDays = (str, days) => {
  const date = parseDate(str);
  if (!date) return str;
  date.setDate(date.getDate() + days);
  return formatDate(date);
};

/**
 * 今日以降で最も近い金曜日（今日が金曜なら今日）。期間を指定せずに開いたときの開始日
 */
export const upcomingFriday = () => {
  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  date.setDate(date.getDate() + ((5 - date.getDay() + 7) % 7));
  return formatDate(date);
};

/**
 * 実在する yyyy-MM-dd か（2026-02-30 のような値は不可）
 */
export const isDateStr = (str) => {
  const date = parseDate(str);
  return !!date && formatDate(date) === str;
};

/**
 * 期間の日数（開始日・終了日を含む）。不正値は 0
 */
export const periodDayCount = (from, to) => {
  const a = parseDate(from);
  const b = parseDate(to);
  if (!a || !b) return 0;
  // 夏時間のない日本でも端数が出ないよう丸める
  return Math.round((b - a) / 86400000) + 1;
};

/**
 * 有効な対象期間か（実在日・開始日≦終了日・最大 MAX_PERIOD_DAYS 日）
 */
export const isValidPeriod = (from, to) => {
  if (!isDateStr(from) || !isDateStr(to)) return false;
  const count = periodDayCount(from, to);
  return count >= 1 && count <= MAX_PERIOD_DAYS;
};

/**
 * 期間の全日付（yyyy-MM-dd の配列）。不正な期間は空配列
 */
export const periodDates = (from, to) => {
  if (!isValidPeriod(from, to)) return [];
  return Array.from({ length: periodDayCount(from, to) }, (_, i) => addDays(from, i));
};

/**
 * 2 つの期間が 1 日でも重なるか
 */
export const periodsOverlap = (a, b) => a.dateFrom <= b.dateTo && b.dateFrom <= a.dateTo;

/**
 * 期間を指定せずに開いたときの期間。直近の金曜日を含む保存済み期間があればそれを、なければ金曜日から 1 週間
 * periods：GET /plan/periods の一覧
 */
export const defaultPeriod = (periods = []) => {
  const start = upcomingFriday();
  const saved = periods.find((p) => p.dateFrom <= start && start <= p.dateTo);
  if (saved) return { dateFrom: saved.dateFrom, dateTo: saved.dateTo };
  return { dateFrom: start, dateTo: addDays(start, DEFAULT_PERIOD_DAYS - 1) };
};

/**
 * URL の query から期間を取り出す（?dateFrom=&dateTo=）。旧形式の ?weekStart= は 1 週間として読む。なければ null
 */
export const periodFromQuery = (query = {}) => {
  const { dateFrom, dateTo, weekStart } = query;
  if (typeof dateFrom === "string" && typeof dateTo === "string" && isValidPeriod(dateFrom, dateTo)) {
    return { dateFrom, dateTo };
  }
  if (typeof weekStart === "string" && isDateStr(weekStart)) {
    return { dateFrom: weekStart, dateTo: addDays(weekStart, DEFAULT_PERIOD_DAYS - 1) };
  }
  return null;
};

/**
 * 日付の短い表示。例：「10/23(金)」
 */
export const shortDateLabel = (str) => {
  const date = parseDate(str);
  if (!date) return str || "";
  return `${date.getMonth() + 1}/${date.getDate()}(${WEEKDAY_LABELS[date.getDay()]})`;
};

/**
 * 対象期間の表示。例：「2026/10/23(金) 〜 10/29(木)」（年をまたぐときは終了日にも年を付ける）
 */
export const periodRangeLabel = (from, to) => {
  const a = parseDate(from);
  const b = parseDate(to);
  if (!a || !b) return "";
  const end = a.getFullYear() === b.getFullYear() ? shortDateLabel(to) : `${b.getFullYear()}/${shortDateLabel(to)}`;
  return `${a.getFullYear()}/${shortDateLabel(from)} 〜 ${end}`;
};

/**
 * 期間の表示（同日なら 1 日だけ）
 */
export const periodLabel = (from, to) => {
  if (!from && !to) return "期間全体";
  if (from === to) return shortDateLabel(from);
  return `${shortDateLabel(from)}〜${shortDateLabel(to)}`;
};

// ==================== バージョン・作品 ====================

/**
 * 上映バージョンの表示名。例：「2D・IMAX・字幕」
 */
export const versionLabel = (v) => {
  if (!v) return "";
  return [v.dimension, v.screenFormat, v.language, v.special].filter(Boolean).join("・");
};

/**
 * スクリーンの上映可能バージョン（属性ごとの複数選択）の表示。例：「2D・3D / 通常・IMAX / 爆音」
 * 属性内はいずれか（OR）、属性間はすべて満たす必要がある。特殊は空なら省略
 */
export const screenCapabilityLabel = (s) => [s?.dimensions, s?.screenFormats, s?.specials]
  .filter((list) => Array.isArray(list) && list.length)
  .map((list) => list.join("・"))
  .join(" / ");

/**
 * スクリーンの上映可能バージョンが設定済みか（2D/3D と上映方式は必須）
 */
export const hasScreenCapability = (s) => !!s?.dimensions?.length && !!s?.screenFormats?.length;

// 作品＋バージョンの色（明暗どちらのテーマでも文字色を変えずに読めるよう、背景は薄く混ぜて使う）
const PALETTE = [
  "#2563eb", "#dc2626", "#16a34a", "#d97706", "#7c3aed", "#0891b2",
  "#db2777", "#65a30d", "#ea580c", "#4f46e5", "#0d9488", "#a21caf",
];

/**
 * パレットの色を返す。12 色を超えたら色相を回して作る
 */
export const paletteColor = (index) => {
  if (index < PALETTE.length) return PALETTE[index];
  return `hsl(${Math.round((index * 137.5) % 360)} 65% 45%)`;
};

/**
 * 作品＋バージョンの色分けキー
 */
export const colorKey = (schedMovieId, versionId) => `${schedMovieId}|${versionId ?? ""}`;

// ==================== 上映回 ====================

/**
 * 上映回の終了時刻・占有終了時刻を計算（表示用。正式な判定はバックエンド）
 * 終了 = 開始 + CM + 予告 + 本編、占有終了 = 終了 + バッファ
 */
export const calcShowTimes = (show) => {
  const start = toMinutes(show.startTime);
  if (start === null) return { start: null, end: null, occupyEnd: null };
  const end = start + (show.adMinute || 0) + (show.trailerMinute || 0) + (show.featureMinute || 0);
  return { start, end, occupyEnd: end + (show.bufferMinute || 0) };
};

/**
 * 上映回の終了時刻を最新の分数で付け直す（ドラッグ・編集後に呼ぶ）
 */
export const refreshShowTimes = (show) => {
  const { end, occupyEnd } = calcShowTimes(show);
  show.endTime = toTime(end);
  show.occupyEndTime = toTime(occupyEnd);
};

/**
 * 保存・チェック API に送る上映回（読み取り専用の項目は送らない）
 */
export const toShowPayload = (show) => ({
  showId: show.showId ?? null,
  screenId: show.screenId,
  schedMovieId: show.schedMovieId,
  versionId: show.versionId,
  bizDate: show.bizDate,
  startTime: show.startTime,
  adMinute: show.adMinute ?? 0,
  trailerMinute: show.trailerMinute ?? 0,
  bufferMinute: show.bufferMinute ?? 0,
  source: show.source || "MANUAL",
});
